import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import {
  PARTNER_TYPES_QUERY,
  USAGE_TYPES_QUERY,
  COUNTIES_QUERY,
  CITIES_QUERY,
  NATIONALITIES_QUERY,
  PERSON_DOCUMENT_TYPES_QUERY,
  MASTER_PARTNER_QUERY
} from '../queries';
import { ACTIVITY_TYPES_QUERY } from '../../../../queries/insuranceDocumentQueries';
import {
  useDictionaryHandling,
  useParams,
  useToast,
  useSimpleError,
  useQueryWithSimpleErrorHandling,
  useClientQueryWithSimpleErrorHandling,
  useAuthUserData,
  useInsuranceTypeConfiguration,
  useDebouncedLensEffect
} from 'hooks';
import { usePartnerType } from 'hooks/document/usePartnerType';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useTranslation } from 'react-i18next';
import {
  isCnp,
  isCif,
  isCnpOrCif,
  isSeriesAndNumber,
  isPhone,
  dateIsGreaterThanGivenDate,
  dateIsLessThanOrEqualToCurrentDate,
  validateDistanceBetweenTwoDates
} from 'utils/simple-validations/validations';
import { fromModel, shape, required, maxLength, email, when, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { getActivityTypes } from 'features/insuranceDocument/edit/utils';
import { Prompt } from 'react-router-dom';
import { isDirty } from '@totalsoft/change-tracking';
import {
  emptyObject,
  emptyCity,
  insuranceTypeNames,
  partnerIdentityDocumentValidityDays,
  defaultDates,
  identityDocumentTypes,
  systemGeneratedUniqueIdPrefix
} from 'constants/defaultValues';
import { cleanUniqueId } from 'utils/functions';
import SearchPartnerContainer from './SearchPartnerContainer';
import PartnerCard from './PartnerCard';

const uniqueIdValidator = (isPF, isPJ, isPFA, partnerTypeId) => {
  return shape({
    uniqueId:
      [required, isCnp |> when(isPF(partnerTypeId)), isCif |> when(isPJ(partnerTypeId)), isCnpOrCif |> when(isPFA(partnerTypeId))]
      |> stopOnFirstFailure
  });
};

const validator = (
  isPF,
  isPJ,
  isPFA,
  isExternal,
  partnerTypeId,
  insuranceTypeCode,
  cascoInsuranceTypeCode,
  identityDocumentTypeCode,
  identityDocumentTypeCodesWithStructureValidation,
  t
) => {
  return fromModel(item => {
    const skipContactPersonUniqueIdValidation =
      isExternal || item?.contactPersonUniqueID?.toUpperCase().startsWith(systemGeneratedUniqueIdPrefix);

    const validateIdentityCardNo =
      !isExternal &&
      identityDocumentTypeCode &&
      identityDocumentTypeCodesWithStructureValidation?.some(x => x === identityDocumentTypeCode);

    return shape({
      partnerTypeId: required,
      uniqueID:
        [
          required,
          maxLength(50),
          isCnp |> when(isPF(partnerTypeId) && !isExternal),
          isCif |> when(isPJ(partnerTypeId) && !isExternal),
          isCnpOrCif |> when(isPFA(partnerTypeId) && !isExternal)
        ] |> stopOnFirstFailure,
      firstName: [required |> when(isPF(partnerTypeId)), maxLength(150)] |> stopOnFirstFailure,
      lastName: [required |> when(isPF(partnerTypeId)), maxLength(150)] |> stopOnFirstFailure,
      partnerName: [required |> when(isPJ(partnerTypeId)), maxLength(200)] |> stopOnFirstFailure,
      phoneNumber: [required, maxLength(50), isPhone(item.phoneCountryCode)] |> stopOnFirstFailure,
      email: [required, maxLength(50), email] |> stopOnFirstFailure,
      drivingLicenseReleaseDate: required |> when(isPF(partnerTypeId)),
      districtId: required,
      city: shape({
        id: required
      }),
      street: [required |> when(insuranceTypeCode === cascoInsuranceTypeCode), maxLength(255)] |> stopOnFirstFailure,
      streetNumber: [required |> when(insuranceTypeCode === cascoInsuranceTypeCode), maxLength(50)] |> stopOnFirstFailure,
      buildingNumber: maxLength(25),
      stairNumber: maxLength(50),
      floorNumber: maxLength(50),
      flatNumber: maxLength(25),
      zipCode: maxLength(20),
      activityFieldId: required |> when(isPJ(partnerTypeId)),
      usageTypeId: required,
      contactPersonUniqueID:
        [
          maxLength(50),
          isCnp |> when(isPFA(partnerTypeId) && !skipContactPersonUniqueIdValidation),
          isCnp |> when(isPJ(partnerTypeId) && !skipContactPersonUniqueIdValidation)
        ] |> stopOnFirstFailure,
      contactPersonName: maxLength(255),
      identityCardNo:
        [maxLength(50), isSeriesAndNumber |> when(Boolean(item.identityCardNo) && Boolean(validateIdentityCardNo))] |> stopOnFirstFailure,
      identityDocumentIssueDate: dateIsLessThanOrEqualToCurrentDate,
      issuedBy: maxLength(255),
      identityDocumentExpireDate:
        [
          dateIsGreaterThanGivenDate(item.identityDocumentIssueDate, t('Partner.IssuedAt')),
          validateDistanceBetweenTwoDates(
            item.identityDocumentIssueDate,
            t('Partner.IssuedAt'),
            t('Partner.ValidUntil'),
            partnerIdentityDocumentValidityDays
          )
        ] |> stopOnFirstFailure
    });
  });
};

const emptyPartner = {
  partnerId: null,
  partnerTypeId: null,
  uniqueID: null,
  agreesWithPersonalDataUse: false,
  partnerCode: null,
  partnerName: null,
  firstName: null,
  lastName: null,
  phoneNumber: null,
  email: null,
  identityDocumentIssueDate: null,
  identityDocumentExpireDate: null,
  contactPersonUniqueID: null,
  contactPersonName: null,
  chContactPersonId: null,
  nationalityId: null,
  citizenshipId: null,
  issuedBy: null,
  identityCardNo: null,
  identityDocumentTypeId: null,
  cityId: null,
  districtId: null,
  street: null,
  streetNumber: null,
  buildingNumber: null,
  stairNumber: null,
  floorNumber: null,
  flatNumber: null,
  zipCode: null,
  fullAddress: null,
  activityFieldId: null,
  usageTypeId: null,
  drivingLicenseReleaseDate: null,
  validationMoment: null,
  masterPartnerValidationMoment: null,
  isSystem: false,
  masterPartnerId: null,
  chPartnerId: null,
  chPersonId: null
};

const PartnerContainer = ({
  title,
  icon,
  insuredIsClientIsDirty,
  systemPartners,
  partner,
  loading,
  reference,
  disabled,
  onSave,
  saving,
  CustomContent,
  insuranceTypeCode,
  globalReadOnly,
  dirtyInfoRef,
  setDirtyInfoRef
}) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const { authUser } = useAuthUserData();
  const { cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(new Array(insuranceTypeNames.CASCO));
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searching, setSearching] = useState({
    partner: false,
    contactPerson: false
  });
  const [searchPartnerModal, setSearchPartnerModal] = useState({});
  const [isNewPartner, setIsNewPartner] = useState(true);
  const [allowSearchByUniqueId, setAllowSearchByUniqueId] = useState({
    partner: false,
    contactPerson: false
  });
  const [processingOCR, setProcessingOCR] = useState(false);
  const clientQuery = useClientQueryWithSimpleErrorHandling();

  const { isPF, isPFA, isPJ } = usePartnerType();
  const individualPartnerType = useParams('IndividualPartnerType');
  const defaultCountryCode = useParams('DefaultCountryCode');
  const identityDocumentTypeCodesWithStructureValidation = useParams('IdentityDocumentTypeCodesWithStructureValidation');

  const [partnerLens, dirtyInfo, resetPartnerLens] = useChangeTrackingLens(emptyObject);
  const effect = value => value.trim();
  useDebouncedLensEffect(partnerLens.partnerName, effect);
  useDebouncedLensEffect(partnerLens.firstName, effect);
  useDebouncedLensEffect(partnerLens.lastName, effect);
  useDebouncedLensEffect(partnerLens.uniqueID, effect);

  if (reference) reference.current = { partner: partnerLens |> get };

  const { loading: partnerTypesLoading, dictionary: partnerTypesData } = useDictionaryHandling(PARTNER_TYPES_QUERY, 'partnerTypes');

  const individualPartnerTypeId = useMemo(() => {
    return partnerTypesData.find(x => x.code === individualPartnerType)?.id;
  }, [partnerTypesData, individualPartnerType]);

  const { loading: activityTypesLoading, data: activityTypesData } = useQueryWithSimpleErrorHandling(ACTIVITY_TYPES_QUERY);
  const {
    loading: usageTypesLoading,
    dictionary: usageTypes,
    getIdByCode: getUsageTypeId
  } = useDictionaryHandling(USAGE_TYPES_QUERY, 'usageTypes');

  const { loading: countiesLoading, data: countiesData } = useQueryWithSimpleErrorHandling(COUNTIES_QUERY);

  const { loading: nationalitiesLoading, data: nationalitiesData } = useQueryWithSimpleErrorHandling(NATIONALITIES_QUERY);

  const { loading: citizenshipsLoading, data: citizenshipsData } = useQueryWithSimpleErrorHandling(NATIONALITIES_QUERY);

  const { loading: personDocumentTypesLoading, data: personDocumentTypesData } =
    useQueryWithSimpleErrorHandling(PERSON_DOCUMENT_TYPES_QUERY);

  const districtId = partnerLens.districtId |> get;

  const identityDocumentTypeCode = useMemo(() => {
    const identityDocumentTypeId = partnerLens.identityDocumentTypeId |> get;
    if (identityDocumentTypeId === null) return null;
    return personDocumentTypesData?.personDocumentTypes?.find(z => z.id === identityDocumentTypeId)?.code;
  }, [partnerLens.identityDocumentTypeId, personDocumentTypesData]);

  const nationalityCode = useMemo(() => {
    const nationalityId = partnerLens.nationalityId |> get;
    if (nationalityId === null) return null;
    return nationalitiesData?.nationalities?.find(z => z.id === nationalityId)?.code;
  }, [partnerLens.nationalityId, nationalitiesData]);

  const isExternal = useMemo(() => {
    return nationalityCode && nationalityCode !== defaultCountryCode;
  }, [nationalityCode, defaultCountryCode]);

  const { loading: citiesLoading, data: citiesData } = useQueryWithSimpleErrorHandling(CITIES_QUERY, {
    variables: { countyId: districtId },
    skip: !districtId
  });

  const validatorPartner = useMemo(
    () =>
      validator(
        isPF,
        isPJ,
        isPFA,
        isExternal,
        partnerLens.partnerTypeId |> get,
        insuranceTypeCode,
        cascoInsuranceTypeConfiguration?.PropertyCode,
        identityDocumentTypeCode,
        identityDocumentTypeCodesWithStructureValidation,
        t
      ),
    [
      isPF,
      isPJ,
      isPFA,
      isExternal,
      insuranceTypeCode,
      partnerLens,
      cascoInsuranceTypeConfiguration?.PropertyCode,
      identityDocumentTypeCode,
      identityDocumentTypeCodesWithStructureValidation,
      t
    ]
  );
  const [validation, validate, resetValidation] = useDirtyFieldValidation(validatorPartner);

  useEffect(() => {
    if (!partner) return;
    resetPartnerLens(partner);
    setIsNewPartner(!partner.uniqueID);
    resetValidation();
  }, [partner, resetPartnerLens, resetValidation]);

  const validatorUniqueId = useMemo(
    () => uniqueIdValidator(isPF, isPJ, isPFA, partnerLens.partnerTypeId |> get),
    [isPF, isPJ, isPFA, partnerLens.partnerTypeId]
  );
  const [, validateUniqueId] = useDirtyFieldValidation(validatorUniqueId);

  const validatorContactPersonUniqueId = useMemo(
    () =>
      uniqueIdValidator(
        () => true,
        () => false,
        () => false
      ),
    []
  );
  const [, validateContactPersonUniqueId] = useDirtyFieldValidation(validatorContactPersonUniqueId);

  const resetPartnerOnUniqueIdChanged = useCallback(
    uniqueId => {
      (partnerLens |> set)({
        ...emptyPartner,
        phoneCountryCode: defaultCountryCode,
        partnerId: partnerLens.partnerId |> get,
        uniqueID: uniqueId,
        partnerTypeId: partnerLens.partnerTypeId |> get,
        agreesWithPersonalDataUse: partnerLens.agreesWithPersonalDataUse |> get
      });
    },
    [partnerLens, defaultCountryCode]
  );

  const searchMasterPartner = useCallback(
    async (uniqueId, partnerTypeId) => {
      const { data } = await clientQuery(MASTER_PARTNER_QUERY, {
        variables: { uniqueId, partnerTypeId },
        fetchPolicy: 'network-only'
      });
      return data && data.masterPartner;
    },
    [clientQuery]
  );

  const setMasterPartnerData = useCallback(
    masterPartner => {
      const usageTypeId = getUsageTypeId(masterPartner.usageTypeCode);
      (partnerLens |> set)({
        ...(partnerLens |> get),
        ...masterPartner,
        partnerId: partnerLens.partnerId |> get, //make sure partnerId and partnerTypeId are not overriden with the ones from masterPartner
        partnerTypeId: partnerLens.partnerTypeId |> get,
        fullAddress: null,
        usageTypeId,
        masterPartnerId: masterPartner.partnerId,
        phoneCountryCode: masterPartner.phoneCountryCode ?? defaultCountryCode,
        districtId: masterPartner.countyId,
        city: {
          id: masterPartner.city?.id,
          name: masterPartner.city?.name
        },
        identityDocumentIssueDate: masterPartner.startDate,
        identityDocumentExpireDate: masterPartner.endDate,
        masterPartnerValidationMoment: masterPartner.validationMoment
      });
    },
    [partnerLens, getUsageTypeId, defaultCountryCode]
  );

  const copyContactPerson = useCallback(
    async (uniqueId, partnerTypeId) => {
      (partnerLens.chContactPersonId |> set)(null);
      setSearching({ ...searching, contactPerson: true });

      const result = await searchMasterPartner(uniqueId, partnerTypeId);

      if (result && result.partnerId != null) {
        (partnerLens |> set)({
          ...(partnerLens |> get),
          contactPersonName: result.firstName + ' ' + result.lastName,
          contactPersonUniqueID: result.uniqueID,
          chContactPersonId: result.chPersonId,
          phoneNumber: result.phoneNumber,
          phoneCountryCode: result.phoneCountryCode,
          email: result.email,
          citizenshipId: result.citizenshipId,
          nationalityId: result.nationalityId,
          identityDocumentTypeId: result.identityDocumentTypeId,
          identityCardNo: result.identityCardNo,
          issuedBy: result.issuedBy,
          identityDocumentIssueDate: result.startDate,
          identityDocumentExpireDate: result.endDate
        });
      }
      setAllowSearchByUniqueId({ ...allowSearchByUniqueId, contactPerson: false });
      setSearching({ ...searching, contactPerson: false });
    },
    [allowSearchByUniqueId, searchMasterPartner, partnerLens, searching]
  );

  const copyPartner = useCallback(
    async (uniqueId, partnerTypeId) => {
      setSearching({ ...searching, partner: true });
      const uniqueIdClean = cleanUniqueId(uniqueId);

      const systemPartner = systemPartners.find(x => cleanUniqueId(x.uniqueID) === uniqueIdClean);

      if (systemPartner) {
        (partnerLens |> set)({
          ...systemPartner,
          partnerId: null,
          isSystem: false
        });
      } else {
        const result = await searchMasterPartner(uniqueId, partnerTypeId);

        if (result && result.partnerId != null) {
          setMasterPartnerData(result);
        } else {
          if (!isNewPartner || (isNewPartner && (partnerLens.masterPartnerId |> get))) {
            resetPartnerOnUniqueIdChanged(uniqueId);
          }
          (partnerLens.masterPartnerId |> set)(null);
          (partnerLens.chPartnerId |> set)(null);
        }
      }
      resetValidation();
      setAllowSearchByUniqueId({ ...allowSearchByUniqueId, partner: false });
      setSearching({ ...searching, partner: false });
    },
    [
      searching,
      searchMasterPartner,
      setMasterPartnerData,
      resetValidation,
      allowSearchByUniqueId,
      partnerLens,
      isNewPartner,
      resetPartnerOnUniqueIdChanged,
      systemPartners
    ]
  );

  const handleCopyPartner = useCallback(
    async partners => {
      if (!partners.some(partner => partner.selected)) {
        return;
      }
      const partner = partners.filter(x => x.selected);
      const data = partner[0];
      if (data.uniqueId !== '' || data.partnerTypeId != null) {
        await copyPartner(data.uniqueId, data.partnerTypeId);
        setShowSearchModal(false);
      }
    },
    [copyPartner, setShowSearchModal]
  );

  const handleCopyContactPerson = useCallback(
    async partners => {
      if (!partners.some(partner => partner.selected)) {
        return;
      }
      const partner = partners.filter(x => x.selected);
      const data = partner[0];
      if (data.uniqueId !== '' || data.partnerTypeId != null) {
        await copyContactPerson(data.uniqueId, data.partnerTypeId);
        setShowSearchModal(false);
      }
    },
    [copyContactPerson, setShowSearchModal]
  );

  const handleUniqueIdChanged = useCallback(
    value => {
      (partnerLens.uniqueID |> set)(value);
      setAllowSearchByUniqueId({ partner: true });
    },
    [partnerLens.uniqueID]
  );

  const handleContactPersonUniqueIdChanged = useCallback(
    value => {
      (partnerLens.contactPersonUniqueID |> set)(value);
      setAllowSearchByUniqueId({ contactPerson: true });
    },
    [partnerLens.contactPersonUniqueID]
  );

  const handleUniqueIdBlur = useCallback(async () => {
    const uniqueId = partnerLens.uniqueID |> get;
    if (!uniqueId) return;
    if (!isExternal && !validateUniqueId({ uniqueId })) {
      return;
    }

    if (uniqueId && isDirty(dirtyInfo.uniqueID) && allowSearchByUniqueId.partner) {
      await copyPartner(uniqueId, partnerLens.partnerTypeId |> get);
    }
  }, [
    validateUniqueId,
    isExternal,
    dirtyInfo.uniqueID,
    partnerLens.uniqueID,
    allowSearchByUniqueId.partner,
    copyPartner,
    partnerLens.partnerTypeId
  ]);

  const handleContactPersonUniqueIdBlur = useCallback(async () => {
    const uniqueId = partnerLens.contactPersonUniqueID |> get;
    if (!uniqueId) return;
    if (!uniqueId?.toUpperCase().startsWith(systemGeneratedUniqueIdPrefix) && !isExternal && !validateContactPersonUniqueId({ uniqueId }))
      return;

    if (uniqueId && isDirty(dirtyInfo.contactPersonUniqueID) && allowSearchByUniqueId.contactPerson) {
      await copyContactPerson(uniqueId, individualPartnerTypeId);
    }
  }, [
    partnerLens.contactPersonUniqueID,
    isExternal,
    validateContactPersonUniqueId,
    copyContactPerson,
    individualPartnerTypeId,
    dirtyInfo,
    allowSearchByUniqueId.contactPerson
  ]);

  const handlePartnerTypeChange = partnerTypeId => {
    (partnerLens.partnerTypeId |> set)(partnerTypeId);
    (partnerLens.uniqueID |> set)(null);
    (partnerLens.partnerName |> set)(null);
    (partnerLens.usageTypeId |> set)(null);
    (partnerLens.districtId |> set)(null);
    (partnerLens.city |> set)(null);
    (partnerLens.street |> set)(null);
    (partnerLens.streetNumber |> set)(null);
    (partnerLens.buildingNumber |> set)(null);
    (partnerLens.stairNumber |> set)(null);
    (partnerLens.floorNumber |> set)(null);
    (partnerLens.flatNumber |> set)(null);
    (partnerLens.zipCode |> set)(null);
    (partnerLens.phoneNumber |> set)(null);
    (partnerLens.phoneCountryCode |> set)(defaultCountryCode);
    (partnerLens.email |> set)(null);
    (partnerLens.identityDocumentIssueDate |> set)(null);
    (partnerLens.identityDocumentExpireDate |> set)(null);
    (partnerLens.nationalityId |> set)(null);
    (partnerLens.citizenshipId |> set)(null);
    (partnerLens.issuedBy |> set)(null);
    (partnerLens.identityCardNo |> set)(null);
    (partnerLens.identityDocumentTypeId |> set)(null);
    (partnerLens.masterPartnerId |> set)(null);
    (partnerLens.chPartnerId |> set)(null);
    (partnerLens.validationMoment |> set)(null);
    (partnerLens.masterPartnerValidationMoment |> set)(null);

    if (isPF(partnerLens.partnerTypeId |> get)) {
      (partnerLens.activityFieldId |> set)(null);
      (partnerLens.agreesWithPersonalDataUse |> set)(false);
      (partnerLens.contactPersonName |> set)(null);
      (partnerLens.contactPersonUniqueID |> set)(null);
      (partnerLens.chContactPersonId |> set)(null);
    }
    if (!isPF(partnerLens.partnerTypeId |> get)) {
      (partnerLens.firstName |> set)(null);
      (partnerLens.lastName |> set)(null);
      (partnerLens.drivingLicenseReleaseDate |> set)(null);
    }
    resetValidation();
  };

  const handleDistrictChange = value => {
    (partnerLens |> set)({
      ...(partnerLens |> get),
      districtId: value,
      city: emptyCity
    });
  };

  useEffect(() => {
    validate(partnerLens |> get, dirtyInfo);
  }, [validate, partnerLens, dirtyInfo]);

  useEffect(() => {
    setDirtyInfoRef('partnerData', isDirty(dirtyInfo) || !!insuredIsClientIsDirty);
    const { onDirtyInfoChange } = dirtyInfoRef.current;
    onDirtyInfoChange && onDirtyInfoChange();
  }, [dirtyInfo, insuredIsClientIsDirty, dirtyInfoRef, setDirtyInfoRef]);

  const handleSave = () => {
    if (!validate(partnerLens |> get)) {
      return;
    }

    const partner = {
      partnerId: partnerLens.partnerId |> get,
      partnerTypeId: partnerLens.partnerTypeId |> get,
      uniqueID: partnerLens.uniqueID |> get,
      agreesWithPersonalDataUse: partnerLens.agreesWithPersonalDataUse |> get,
      partnerCode: partnerLens.partnerCode |> get,
      partnerName: partnerLens.partnerName |> get,
      firstName: partnerLens.firstName |> get,
      lastName: partnerLens.lastName |> get,
      contactPersonName: partnerLens.contactPersonName |> get,
      contactPersonUniqueID: partnerLens.contactPersonUniqueID |> get,
      chContactPersonId: partnerLens.chContactPersonId |> get,
      citizenshipId: partnerLens.citizenshipId |> get,
      nationalityId: partnerLens.nationalityId |> get,
      identityCardNo: partnerLens.identityCardNo |> get,
      issuedBy: partnerLens.issuedBy |> get,
      identityDocumentIssueDate: partnerLens.identityDocumentIssueDate |> get,
      identityDocumentExpireDate: partnerLens.identityDocumentExpireDate |> get,
      identityDocumentTypeId: partnerLens.identityDocumentTypeId |> get,
      phoneNumber: partnerLens.phoneNumber |> get,
      phoneCountryCode: partnerLens.phoneCountryCode |> get,
      email: partnerLens.email |> get,
      cityId: partnerLens.city.id |> get,
      districtId: partnerLens.districtId |> get,
      street: partnerLens.street |> get,
      streetNumber: partnerLens.streetNumber |> get,
      buildingNumber: partnerLens.buildingNumber |> get,
      stairNumber: partnerLens.stairNumber |> get,
      floorNumber: partnerLens.floorNumber |> get,
      flatNumber: partnerLens.flatNumber |> get,
      zipCode: partnerLens.zipCode |> get,
      fullAddress: partnerLens.fullAddress |> get,
      activityFieldId: partnerLens.activityFieldId |> get,
      usageTypeId: partnerLens.usageTypeId |> get,
      drivingLicenseReleaseDate: partnerLens.drivingLicenseReleaseDate |> get,
      validationMoment: partnerLens.validationMoment |> get,
      masterPartnerValidationMoment: partnerLens.masterPartnerValidationMoment |> get,
      isSystem: partnerLens.isSystem |> get,
      masterPartnerId: partnerLens.masterPartnerId |> get,
      chPartnerId: partnerLens.chPartnerId |> get
    };

    onSave(partner);
  };

  const getLoadingStatus = useCallback(() => {
    return {
      loading,
      partnerTypesLoading,
      activityTypesLoading,
      usageTypesLoading,
      countiesLoading,
      citiesLoading,
      nationalitiesLoading,
      citizenshipsLoading,
      personDocumentTypesLoading
    };
  }, [
    loading,
    partnerTypesLoading,
    activityTypesLoading,
    usageTypesLoading,
    countiesLoading,
    citiesLoading,
    nationalitiesLoading,
    citizenshipsLoading,
    personDocumentTypesLoading
  ]);

  const disabledStatus = useMemo(() => {
    const isSystemPartner = partnerLens.isSystem |> get;
    const agreesWithPersonalDataUse = partnerLens.agreesWithPersonalDataUse |> get;
    const readOnly =
      saving ||
      loading ||
      disabled ||
      searching.partner ||
      isSystemPartner ||
      (isPF(partnerLens.partnerTypeId |> get) && !agreesWithPersonalDataUse) ||
      processingOCR;

    const fullReadOnly = readOnly || Boolean(partnerLens.chPartnerId |> get);
    const contactPersonReadOnly = readOnly || searching.contactPerson || Boolean(partnerLens.chContactPersonId |> get);

    return {
      readonly: readOnly,
      saveDisabled: (readOnly || !isDirty(dirtyInfo)) && !insuredIsClientIsDirty,
      partnerTypes: saving || disabled || searching.partner || isSystemPartner,
      agreesWithPersonalDataUse: disabled || searching.partner || isSystemPartner,
      addressDisabled: fullReadOnly,
      saving: saving,
      contactPersonReadOnly,
      fullReadOnly,
      globalReadOnly
    };
  }, [
    partnerLens.isSystem,
    partnerLens.agreesWithPersonalDataUse,
    partnerLens.partnerTypeId,
    partnerLens.chPartnerId,
    partnerLens.chContactPersonId,
    saving,
    loading,
    disabled,
    searching.partner,
    searching.contactPerson,
    isPF,
    processingOCR,
    dirtyInfo,
    insuredIsClientIsDirty,
    globalReadOnly
  ]);

  const handleOpenSearchPartnersModal = useCallback(
    (show, isContactPerson) => {
      if (isContactPerson) {
        setSearchPartnerModal({
          isPF: () => {
            return true;
          },
          partnerTypeId: individualPartnerTypeId,
          onCopyPartner: handleCopyContactPerson
        });
      } else {
        setSearchPartnerModal({
          isPF: isPF,
          partnerTypeId: partnerLens.partnerTypeId |> get,
          onCopyPartner: handleCopyPartner
        });
      }

      setShowSearchModal(show);
    },
    [isPF, partnerLens.partnerTypeId, handleCopyPartner, handleCopyContactPerson, individualPartnerTypeId, setSearchPartnerModal]
  );

  const showError = useSimpleError();
  const nullifyDate = date => (defaultDates.includes(date) ? null : date);

  const ocrEvents = {
    onStarting: event => {
      const { processingOCR } = event;
      setProcessingOCR(processingOCR);
    },
    onCompleted: async event => {
      const { processingOCR, ocrData } = event;

      if (ocrData.success === false) {
        let error = t('Errors.OCR.Failed');
        if (ocrData.errors && ocrData.errors.length > 0) {
          error = `${error} ${ocrData.errors.join('; ')}`;
        }
        addToast(error, 'error', { toastId: 'ocrErrorToast' });
        setProcessingOCR(processingOCR);
        return;
      }

      const { firstName, lastName, cNP, issuedByName, number, series, address, issuedDate, valabilityDate, country } = ocrData.info;
      const { city, county } = address || {};

      resetPartnerOnUniqueIdChanged(cNP);

      if (!cNP) {
        addToast(t('Errors.OCR.NoUniqueId'), 'error', { toastId: 'ocrNoUniqueIdToast' });
        return;
      }

      const existingPartner = await searchMasterPartner(cNP, partnerLens.partnerTypeId |> get);
      if (existingPartner && existingPartner.partnerId != null) {
        setMasterPartnerData(existingPartner);
      }

      if (firstName) (partnerLens.firstName |> set)(firstName);
      if (lastName) (partnerLens.lastName |> set)(lastName);

      const identityDocumentTypeId =
        personDocumentTypesData?.personDocumentTypes?.find(z => z.code === identityDocumentTypes.CI)?.id || null;
      (partnerLens.identityDocumentTypeId |> set)(identityDocumentTypeId);
      if (series || number) {
        let identityCardNo = null;
        if (series) identityCardNo = series;
        if (number) {
          identityCardNo = `${identityCardNo || ''}${number}`;
        }
        (partnerLens.identityCardNo |> set)(identityCardNo);
      }
      if (issuedByName) (partnerLens.issuedBy |> set)(issuedByName);
      if (nullifyDate(issuedDate)) (partnerLens.identityDocumentIssueDate |> set)(issuedDate);
      if (nullifyDate(valabilityDate)) (partnerLens.identityDocumentExpireDate |> set)(valabilityDate);

      (partnerLens.fullAddress |> set)(address?.full);
      if (address) {
        if (address.street) (partnerLens.street |> set)(address.street);
        if (address.streetNumber) (partnerLens.streetNumber |> set)(address.streetNumber);
        if (address.buildingNumber) (partnerLens.buildingNumber |> set)(address.buildingNumber);
        if (address.stair) (partnerLens.stairNumber |> set)(address.stair);
        if (address.floor) (partnerLens.floorNumber |> set)(address.floor);
        if (address.apartment) (partnerLens.flatNumber |> set)(address.apartment);
      }

      if (city) {
        const _city = citiesData?.cities?.find(z => z.name.toLowerCase() === city.toLowerCase());
        if (_city) (partnerLens.city |> set)(_city);
      }

      if (county) {
        const _district = countiesData?.counties?.find(z => z.name.toLowerCase() === county.toLowerCase());
        if (_district) (partnerLens.districtId |> set)(_district.id);
      }

      if (country) {
        const _citizenship = citizenshipsData?.nationalities?.find(z => country.toLowerCase().startsWith(z.name.toLowerCase()));
        const _nationality = nationalitiesData?.nationalities?.find(z => country.toLowerCase().startsWith(z.name.toLowerCase()));
        if (_citizenship) (partnerLens.citizenshipId |> set)(_citizenship.id);
        if (_nationality) (partnerLens.nationalityId |> set)(_nationality.id);
      }
      setProcessingOCR(processingOCR);
    },
    onError: event => {
      const { processingOCR, error } = event;
      showError(error), setProcessingOCR(processingOCR);
    }
  };

  const ocrContext = { processingOCR: false, metadata: { target: title }, events: ocrEvents };

  return (
    <>
      <Prompt when={isDirty(dirtyInfo) || !!insuredIsClientIsDirty} message={() => t('LeavingWithoutSaving')} />
      <PartnerCard
        partnerLens={partnerLens}
        insuranceTypeCode={insuranceTypeCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
        partnerTypes={partnerTypesData}
        disabledStatus={disabledStatus}
        title={`Partner.${title}`}
        icon={icon}
        activityTypes={getActivityTypes(activityTypesData?.activityTypes)}
        usageTypes={usageTypes}
        counties={countiesData?.counties}
        cities={citiesData?.cities}
        citizenships={citizenshipsData?.nationalities}
        nationalities={nationalitiesData?.nationalities}
        defaultCountryCode={defaultCountryCode}
        personDocumentTypes={personDocumentTypesData?.personDocumentTypes}
        isPF={isPF}
        hasRightToSearch={authUser?.authorizations?.hasRightToSearchMasterPartner ?? false}
        validation={validation}
        CustomContent={CustomContent}
        loadingStatus={getLoadingStatus()}
        onSave={handleSave}
        onUniqueIdBlur={handleUniqueIdBlur}
        onContactPersonUniqueIdBlur={handleContactPersonUniqueIdBlur}
        onUniqueIdChanged={handleUniqueIdChanged}
        onContactPersonUniqueIdChanged={handleContactPersonUniqueIdChanged}
        onPartnerTypeChange={handlePartnerTypeChange}
        onDistrictChange={handleDistrictChange}
        onOpenSearchPartnersModal={handleOpenSearchPartnersModal}
        ocrContext={ocrContext}
      />
      {showSearchModal && (
        <SearchPartnerContainer
          isPF={searchPartnerModal.isPF}
          partnerTypeId={partnerLens.partnerTypeId |> get}
          counties={countiesData?.counties}
          countiesLoading={countiesLoading}
          onCloseModal={() => setShowSearchModal(false)}
          onCopyPartner={searchPartnerModal.onCopyPartner}
        />
      )}
    </>
  );
};

PartnerContainer.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  insuredIsClientIsDirty: PropTypes.bool,
  systemPartners: PropTypes.array,
  partner: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  reference: PropTypes.object,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  CustomContent: PropTypes.object,
  insuranceTypeCode: PropTypes.string,
  globalReadOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default PartnerContainer;
